/**
 * Клиентское отслеживание «прочитанных» комментариев.
 * Хранит количество комментариев, которое пользователь видел при последнем визите на пост.
 * Используется для подсветки карточек с новыми комментариями.
 */

const STORAGE_KEY = 'hrnb:post-read-state';
const MAX_ENTRIES = 200;

/**
 * Получить карту { postId → lastSeenCommentCount } из localStorage.
 * @returns {Object<string, number>}
 */
function getReadStateMap() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

/**
 * Сохранить карту в localStorage (с ограничением количества записей).
 * @param {Object<string, number>} map
 */
function saveReadStateMap(map) {
    try {
        const keys = Object.keys(map);
        if (keys.length > MAX_ENTRIES) {
            // Удаляем самые старые записи (по предположению — первые добавленные)
            const toRemove = keys.length - MAX_ENTRIES;
            for (let i = 0; i < toRemove; i++) {
                delete map[keys[i]];
            }
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch { /* ignore quota errors */ }
}

/**
 * Получить количество комментариев, которое пользователь видел при последнем визите.
 * @param {string|number} postId
 * @returns {number|null} — null, если пост ещё не посещался
 */
export function getLastSeenCommentCount(postId) {
    const map = getReadStateMap();
    return map[postId] ?? null;
}

/**
 * Запомнить текущее количество комментариев для поста.
 * Вызывается при посещении страницы поста.
 * @param {string|number} postId
 * @param {number} commentCount
 */
export function markPostAsRead(postId, commentCount) {
    if (!postId || commentCount == null) return;
    const map = getReadStateMap();
    map[postId] = commentCount;
    saveReadStateMap(map);
}

/**
 * Проверить, есть ли новые комментарии с момента последнего визита.
 * @param {string|number} postId
 * @param {number} currentCommentCount
 * @returns {boolean}
 */
export function hasUnreadComments(postId, currentCommentCount) {
    const lastSeen = getLastSeenCommentCount(postId);
    if (lastSeen === null) return false;
    return currentCommentCount > lastSeen;
}

/**
 * Количество новых комментариев с момента последнего визита.
 * @param {string|number} postId
 * @param {number} currentCommentCount
 * @returns {number}
 */
export function getUnreadCommentCount(postId, currentCommentCount) {
    const lastSeen = getLastSeenCommentCount(postId);
    if (lastSeen === null) return 0;
    return Math.max(0, currentCommentCount - lastSeen);
}
